import isEqual from 'lodash-es/isEqual';
import { SoilTexture } from '../../resources/plots';
/**
 * Algorithm from v3
 * used to get a soil texture from a soil composition
 */
export const getSoilTextureFromComposition = ({ sand, clay, silt }) => {
    let texture;
    if (silt + 1.5 * clay < 15) {
        texture = SoilTexture.sand;
    }
    else if (silt + 1.5 * clay >= 15 && silt + 2 * clay < 30) {
        texture = SoilTexture.loamySand;
    }
    else if ((clay >= 7 && clay < 20 && sand > 52 && silt + 2 * clay >= 30) ||
        (clay < 7 && silt < 50 && silt + 2 * clay >= 30)) {
        texture = SoilTexture.sandyLoam;
    }
    else if (clay >= 7 && clay < 27 && silt >= 28 && silt < 50 && sand <= 52) {
        texture = SoilTexture.loam;
    }
    else if ((silt >= 50 && clay >= 12 && clay < 27) || (silt >= 50 && silt < 80 && clay < 12)) {
        texture = SoilTexture.siltLoam;
    }
    else if (silt >= 80 && clay < 12) {
        texture = SoilTexture.silt;
    }
    else if (clay >= 20 && clay < 35 && silt < 28 && sand > 45) {
        texture = SoilTexture.sandyClayLoam;
    }
    else if (clay >= 27 && clay < 40 && sand > 20 && sand <= 45) {
        texture = SoilTexture.clayLoam;
    }
    else if (clay >= 27 && clay < 40 && sand <= 20) {
        texture = SoilTexture.siltyClayLoam;
    }
    else if (clay >= 35 && sand > 45) {
        texture = SoilTexture.sandyClay;
    }
    else if (clay >= 40 && silt >= 40) {
        texture = SoilTexture.siltClay;
    }
    else if (clay >= 40 && sand <= 45 && silt < 40) {
        texture = SoilTexture.clay;
    }
    else {
        texture = undefined;
    }
    return texture;
};
/**
 * soil composition is a percentage of silt sand and clay so we can deduce silt from clay and sand
 */
export function computeSilt({ sand, clay }) {
    return 100 - (clay + sand);
}
/** TODO: we should had a nice crazy type to ensure subfields addition is less than 100 {@see {@link https://blog.kbdev.io/dev/2021/10/05/type-calculator.html}} */
export const defaultTextureComposition = {
    [SoilTexture.clay]: { clay: 64, sand: 18, silt: 18 },
    [SoilTexture.clayLoam]: { clay: 33, sand: 33, silt: 34 },
    [SoilTexture.loam]: { clay: 18, sand: 41, silt: 41 },
    [SoilTexture.loamySand]: { clay: 6, sand: 82, silt: 12 },
    [SoilTexture.sand]: { clay: 4, sand: 92, silt: 4 },
    [SoilTexture.sandyClay]: { clay: 42, sand: 52, silt: 6 },
    [SoilTexture.sandyClayLoam]: { clay: 28, sand: 60, silt: 12 },
    [SoilTexture.sandyLoam]: { clay: 10, sand: 65, silt: 25 },
    [SoilTexture.silt]: { clay: 6, sand: 7, silt: 87 },
    [SoilTexture.siltClay]: { clay: 47, sand: 6, silt: 47 },
    [SoilTexture.siltyClayLoam]: { clay: 33, sand: 10, silt: 57 },
    [SoilTexture.siltLoam]: { clay: 13, sand: 22, silt: 65 }
};
/**
 * Map default value of tensiometers irrigation thresholds for a given soil texture
 * @deprecated use oad/irritensio/default_thresholds endpoint instead
 */
export const defaultSoilTextureIrritensioThresholds = {
    [SoilTexture.clay]: { saturation: 0, comfort: 10, vigilance: 42, stress: 170 },
    [SoilTexture.clayLoam]: { saturation: 0, comfort: 15, vigilance: 53, stress: 107 },
    [SoilTexture.loam]: { saturation: 0, comfort: 10, vigilance: 50, stress: 100 },
    [SoilTexture.loamySand]: { saturation: 0, comfort: 14, vigilance: 35, stress: 59 },
    [SoilTexture.sand]: { saturation: 0, comfort: 6, vigilance: 8, stress: 10 },
    [SoilTexture.sandyClay]: { saturation: 0, comfort: 8, vigilance: 65, stress: 155 },
    [SoilTexture.sandyClayLoam]: { saturation: 0, comfort: 15, vigilance: 75, stress: 155 },
    [SoilTexture.sandyLoam]: { saturation: 0, comfort: 15, vigilance: 58, stress: 110 },
    [SoilTexture.silt]: { saturation: 0, comfort: 25, vigilance: 75, stress: 126 },
    [SoilTexture.siltClay]: { saturation: 0, comfort: 14, vigilance: 62, stress: 135 },
    [SoilTexture.siltyClayLoam]: { saturation: 0, comfort: 20, vigilance: 75, stress: 140 },
    [SoilTexture.siltLoam]: { saturation: 0, comfort: 20, vigilance: 72, stress: 126 }
};
/**
 * Map default value of capacitive irrigation thresholds for a given soil texture
 * @deprecated use oad/irritensio/default_thresholds endpoint instead
 */
export const defaultSoilTextureCapacitiveIrrigationThresholds = {
    [SoilTexture.clay]: { saturation: 42, comfort: 31, vigilance: 27, stress: 19 },
    [SoilTexture.clayLoam]: { saturation: 36, comfort: 24, vigilance: 20, stress: 12 },
    [SoilTexture.loam]: { saturation: 29, comfort: 19, vigilance: 16, stress: 9 },
    [SoilTexture.loamySand]: { saturation: 13, comfort: 9, vigilance: 8, stress: 5 },
    [SoilTexture.sand]: { saturation: 11, comfort: 8, vigilance: 7, stress: 5 },
    [SoilTexture.sandyClay]: { saturation: 36, comfort: 27, vigilance: 25, stress: 19 },
    [SoilTexture.sandyClayLoam]: { saturation: 27, comfort: 19, vigilance: 16, stress: 11 },
    [SoilTexture.sandyLoam]: { saturation: 18, comfort: 12, vigilance: 10, stress: 6 },
    [SoilTexture.silt]: { saturation: 29, comfort: 18, vigilance: 14, stress: 7 },
    [SoilTexture.siltClay]: { saturation: 41, comfort: 29, vigilance: 25, stress: 17 },
    [SoilTexture.siltyClayLoam]: { saturation: 39, comfort: 26, vigilance: 21, stress: 12 },
    [SoilTexture.siltLoam]: { saturation: 32, comfort: 21, vigilance: 17, stress: 9 }
};
export const isCustomThresholds = ({ currentThresholds, soilTexture, isIrritensio }) => {
    const defaultThreshold = isIrritensio
        ? defaultSoilTextureIrritensioThresholds
        : defaultSoilTextureCapacitiveIrrigationThresholds;
    return (soilTexture === SoilTexture.custom ||
        (currentThresholds && !isEqual(defaultThreshold[soilTexture], currentThresholds)));
};
export const triangleWidth = 322;
export const triangleHeight = 277;
export const imageLeftAndTriangle = triangleWidth + 13; // 13px from left to triangle corner
export const imageTopAndTriangle = triangleHeight + 14; // 14px from top to triangle corner
/**
 * Get percentages of clay, sand and silt with clicked coordinates
 */
export function coordsToPercent({ x, y }) {
    let clay = ((imageTopAndTriangle - y) * 100) / triangleHeight;
    let sand = ((imageLeftAndTriangle - x - (0.5 * triangleWidth * clay) / 100) * 100) / triangleWidth;
    clay = Math.floor(clay);
    sand = Math.floor(sand);
    const silt = computeSilt({ sand, clay });
    return { clay, sand, silt };
}
/**
 * get composition from coords in triangle
 */
export function percentToCoords({ clay, sand }) {
    const y = imageTopAndTriangle - (clay * triangleHeight) / 100;
    const x = imageLeftAndTriangle - (sand * triangleWidth) / 100 - (0.5 * triangleWidth * clay) / 100;
    return { x, y };
}
/** Return a {@link TKey} for a given SoilTexture */
export function textureTKey(soilTexture) {
    return `soilTextures.${SoilTexture[soilTexture]}`;
}
